import type {
  PpeRequiredItem,
  PpeActiveIssuance,
  PpeHistoryItem,
  PpeEmployeeProfile,
  SizCatalogItem,
  SizCategory,
  SizUnit,
} from '@/entities/siz';

/**
 * Демо-данные для вкладки СИЗ. Покрывают все статусы (`issued`, `partially_issued`,
 * `expiring_soon`, `overdue`, `pending`), все категории каталога и разные подписи
 * строки (с/без инв. номера, с/без размера, с/без следующей выдачи).
 *
 * Активируется флагом `SIZ_TAB_USE_MOCKS` в `SizTab.tsx`.
 */

const ITEM_IDS = {
  weldingSuit: '11111111-1111-1111-1111-000000000001',
  boots: '11111111-1111-1111-1111-000000000002',
  gloves: '11111111-1111-1111-1111-000000000003',
  helmet: '11111111-1111-1111-1111-000000000004',
  weldingShield: '11111111-1111-1111-1111-000000000005',
  respirator: '11111111-1111-1111-1111-000000000006',
  winterJacket: '11111111-1111-1111-1111-000000000007',
  earmuffs: '11111111-1111-1111-1111-000000000008',
  fallHarness: '11111111-1111-1111-1111-000000000009',
  toolBag: '11111111-1111-1111-1111-00000000000a',
  safetyGlasses: '11111111-1111-1111-1111-00000000000b',
} as const;

function requiredItem(input: {
  sizItemId: string;
  name: string;
  category: SizCategory;
  unit: SizUnit;
  requiredQuantity: string;
  issuedQuantity: string;
  status: PpeRequiredItem['status'];
  nextReplacementAt?: string;
}): PpeRequiredItem {
  return { nextReplacementAt: undefined, ...input };
}

function catalogItem(input: {
  id: string;
  code: string;
  name: string;
  category: SizCategory;
  unit: SizUnit;
}): SizCatalogItem {
  return {
    ...input,
    gostOrTrTs: undefined,
    defaultValidityMonths: undefined,
    isActive: true,
  };
}

function issuance(input: {
  id: string;
  sizItem: SizCatalogItem;
  issuedAt: string;
  quantity: string;
  validUntil: string;
  daysRemaining: number;
  size?: string;
  inventoryNumber?: string;
}): PpeActiveIssuance {
  return { size: undefined, inventoryNumber: undefined, ...input };
}

function historyItem(input: {
  id: string;
  type: PpeHistoryItem['type'];
  sizItemName: string;
  date: string;
  quantity: string;
  size?: string;
  reason?: string;
}): PpeHistoryItem {
  return { size: undefined, reason: undefined, ...input };
}

export const MOCK_PROFILE_SIZES: Record<string, string> = {
  height: '182',
  clothing: '52 / III',
  footwear: '43',
  headwear: '58',
  respirator: 'M',
};

export const MOCK_PROFILE: PpeEmployeeProfile = {
  id: '00000000-0000-0000-0000-000000000000',
  fullName: 'Корнев Сергей Михайлович',
  tabNumber: 'SEED-000142',
  isActive: true,
  position: { id: '00000000-0000-0000-0000-000000000010', name: 'Сварщик ручной дуговой сварки' },
  orgUnit: { id: '00000000-0000-0000-0000-000000000020', name: 'Сборочно-сварочный цех' },
  sizes: MOCK_PROFILE_SIZES,
};

export const MOCK_REQUIRED_ITEMS: PpeRequiredItem[] = [
  requiredItem({
    sizItemId: ITEM_IDS.weldingSuit,
    name: 'Костюм сварщика',
    category: 'clothing',
    unit: 'kit',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'issued',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.boots,
    name: 'Ботинки кожаные с подноском',
    category: 'footwear',
    unit: 'pair',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'expiring_soon',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.gloves,
    name: 'Перчатки спилковые',
    category: 'hand_protection',
    unit: 'pair',
    requiredQuantity: '12',
    issuedQuantity: '8',
    status: 'partially_issued',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.helmet,
    name: 'Каска защитная',
    category: 'head_protection',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'issued',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.weldingShield,
    name: 'Щиток сварщика',
    category: 'eye_protection',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'issued',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.respirator,
    name: 'Респиратор FFP2',
    category: 'respiratory_protection',
    unit: 'piece',
    requiredQuantity: '30',
    issuedQuantity: '5',
    status: 'expiring_soon',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.winterJacket,
    name: 'Куртка зимняя',
    category: 'clothing',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '0',
    status: 'pending',
    nextReplacementAt: '2026-11-01',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.earmuffs,
    name: 'Наушники противошумные',
    category: 'hearing_protection',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '0',
    status: 'pending',
    nextReplacementAt: '2025-09-15',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.fallHarness,
    name: 'Пояс предохранительный',
    category: 'fall_protection',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'overdue',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.toolBag,
    name: 'Сумка для инструмента',
    category: 'other',
    unit: 'piece',
    requiredQuantity: '1',
    issuedQuantity: '1',
    status: 'issued',
  }),
  requiredItem({
    sizItemId: ITEM_IDS.safetyGlasses,
    name: 'Очки защитные открытые',
    category: 'eye_protection',
    unit: 'piece',
    requiredQuantity: '2',
    issuedQuantity: '2',
    status: 'issued',
  }),
];

export const MOCK_ISSUANCES: PpeActiveIssuance[] = [
  issuance({
    id: '22222222-2222-2222-2222-000000000001',
    sizItem: catalogItem({
      id: ITEM_IDS.weldingSuit,
      code: 'СВ-К-01',
      name: 'Костюм сварщика',
      category: 'clothing',
      unit: 'kit',
    }),
    issuedAt: '2025-09-15',
    quantity: '1',
    size: '52/III',
    validUntil: '2026-09-15',
    daysRemaining: 125,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000002',
    sizItem: catalogItem({
      id: ITEM_IDS.boots,
      code: 'ОБ-К-01',
      name: 'Ботинки кожаные с подноском',
      category: 'footwear',
      unit: 'pair',
    }),
    issuedAt: '2025-05-22',
    quantity: '1',
    size: '43',
    validUntil: '2026-05-22',
    daysRemaining: 12,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000003',
    sizItem: catalogItem({
      id: ITEM_IDS.helmet,
      code: 'КАС-01',
      name: 'Каска защитная',
      category: 'head_protection',
      unit: 'piece',
    }),
    issuedAt: '2025-09-15',
    quantity: '1',
    size: '58',
    inventoryNumber: 'К-2148',
    validUntil: '2028-09-15',
    daysRemaining: 855,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000004',
    sizItem: catalogItem({
      id: ITEM_IDS.weldingShield,
      code: 'ЩИТ-01',
      name: 'Щиток сварщика',
      category: 'eye_protection',
      unit: 'piece',
    }),
    issuedAt: '2025-09-15',
    quantity: '1',
    inventoryNumber: 'Щ-0488',
    validUntil: '2028-09-15',
    daysRemaining: 855,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000005',
    sizItem: catalogItem({
      id: ITEM_IDS.respirator,
      code: 'РЕС-FFP2',
      name: 'Респиратор FFP2',
      category: 'respiratory_protection',
      unit: 'piece',
    }),
    issuedAt: '2026-04-02',
    quantity: '5',
    validUntil: '2026-05-25',
    daysRemaining: 12,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000006',
    sizItem: catalogItem({
      id: ITEM_IDS.fallHarness,
      code: 'ПОЯС-01',
      name: 'Пояс предохранительный',
      category: 'fall_protection',
      unit: 'piece',
    }),
    issuedAt: '2024-03-10',
    quantity: '1',
    inventoryNumber: 'П-0177',
    validUntil: '2026-03-10',
    daysRemaining: -64,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000007',
    sizItem: catalogItem({
      id: ITEM_IDS.toolBag,
      code: 'СУМ-01',
      name: 'Сумка для инструмента',
      category: 'other',
      unit: 'piece',
    }),
    issuedAt: '2025-09-15',
    quantity: '1',
    validUntil: '2027-09-15',
    daysRemaining: 490,
  }),
  issuance({
    id: '22222222-2222-2222-2222-000000000008',
    sizItem: catalogItem({
      id: ITEM_IDS.safetyGlasses,
      code: 'ОЧК-01',
      name: 'Очки защитные открытые',
      category: 'eye_protection',
      unit: 'piece',
    }),
    issuedAt: '2025-09-15',
    quantity: '2',
    validUntil: '2026-09-15',
    daysRemaining: 125,
  }),
];

export const MOCK_HISTORY: PpeHistoryItem[] = [
  historyItem({
    id: '33333333-3333-3333-3333-000000000001',
    type: 'issuance',
    sizItemName: 'Перчатки спилковые',
    date: '2026-04-03',
    quantity: '2',
    size: 'L',
  }),
  historyItem({
    id: '33333333-3333-3333-3333-000000000002',
    type: 'issuance',
    sizItemName: 'Респиратор FFP2',
    date: '2026-04-02',
    quantity: '5',
  }),
  historyItem({
    id: '33333333-3333-3333-3333-000000000003',
    type: 'return',
    sizItemName: 'Каска защитная',
    date: '2025-09-15',
    quantity: '1',
    size: '58',
    reason: 'Износ',
  }),
  historyItem({
    id: '33333333-3333-3333-3333-000000000004',
    type: 'issuance',
    sizItemName: 'Костюм сварщика',
    date: '2025-09-15',
    quantity: '1',
    size: '52/III',
  }),
  historyItem({
    id: '33333333-3333-3333-3333-000000000005',
    type: 'issuance',
    sizItemName: 'Ботинки кожаные с подноском',
    date: '2025-05-22',
    quantity: '1',
    size: '43',
  }),
  historyItem({
    id: '33333333-3333-3333-3333-000000000006',
    type: 'return',
    sizItemName: 'Костюм сварщика (старый)',
    date: '2025-09-15',
    quantity: '1',
    reason: 'Истёк срок носки',
  }),
];
